import { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronDown, ExternalLink, Search } from "lucide-react";
import { ShieldCheck,WifiOff, Fingerprint, BarChart3, LucideIcon, Rocket} from "lucide-react";
import { useTranslation } from "react-i18next";
interface Feature {
    icon: LucideIcon;
    title: string;
    desc: string;
}

/* ---------------- Region / Language ---------------- */

type LangCode = "en" | "es" | "it";
function RegionLanguageMenu({
    value,
    onChange,
}: {
    value: LangCode;
    onChange: (lang: LangCode) => void;
}) {
    const [open, setOpen] = useState(false);
    const btnRef = useRef<HTMLButtonElement | null>(null);
    const panelRef = useRef<HTMLDivElement | null>(null);

    const options: Array<{ country: string; label: string; code: LangCode }> = [
        { country: "United States", label: "English", code: "en" },
        { country: "United States", label: "Spanish", code: "es" },
        { country: "United States", label: "Italian", code: "it" },
    ];

    const current =
        options.find((o) => o.code === value) ?? {
            country: "United States",
            label: "English",
            code: "en",
        };

    // Close on outside click / ESC
    useEffect(() => {
        const onDocDown = (e: MouseEvent) => {
            const t = e.target as Node;
            if (btnRef.current?.contains(t) || panelRef.current?.contains(t)) return;
            setOpen(false);
        };
        const onEsc = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        document.addEventListener("mousedown", onDocDown);
        document.addEventListener("keydown", onEsc);
        return () => {
            document.removeEventListener("mousedown", onDocDown);
            document.removeEventListener("keydown", onEsc);
        };
    }, []);

    return (
        <div className="relative">
            <button
                ref={btnRef}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50"
                aria-haspopup="menu"
                aria-expanded={open}
            >
                {current.label}
                <ChevronDown
                    size={14}
                    className={`transition-transform ${open ? "rotate-180" : ""}`}
                />
            </button>

            {open && (
                <div
                    ref={panelRef}
                    className="absolute right-0 mt-2 w-56 rounded-md border bg-white shadow-xl ring-1 ring-black/5 z-[10000]"
                >
                    {options.map((o) => (
                        <button
                            key={o.code}
                            onClick={() => {
                                onChange(o.code);
                                setOpen(false);
                            }}
                            className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-50 ${value === o.code ? "bg-slate-100 font-medium" : ""
                                }`}
                        >
                            {o.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
/* ---------------- Menu Model ---------------- */
const MENU = [
    {
        label: "Features",
        children: [
            { label: "Build Exams", to: "/solutions/examspro/build" },
            { label: "Deliver Exams", to: "/solutions/examspro/deliver" },
            { label: "Biometric Validation", to: "/solutions/examspro/biometric" },
            { label: "Offline Delivery", to: "/solutions/examspro/offline" },
            { label: "Performance Analytics", to: "/solutions/examspro/analytics" },
        ],
    },
    {
        label: "Services",
        children: [
            { label: "Exam Design & Development", to: "/services/exam-design" },
            { label: "Test Security Services", to: "/services/security" },
        ],
    },
    {
        label: "Resources",
        children: [
            { label: "Help Center", to: "/resources/help" },
            { label: "Resource Library", to: "/resources/library" },
            { label: "Training", to: "/resources/training" },
            { label: "FAQ", to: "/resources/faq" }


        ],
    },
    {
        label: "About",
        children: [
            { label: "Why SecureID", to: "/about/why-secureid" },
            { label: "Contact Us", to: "/solutions/examspro/support" },

        ],
    },

];

function LoginPortal() {
    return (
        <section className="bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 py-4 flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center">
                <Link
                    to="/login"
                    className="w-full sm:w-auto text-center bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-indigo-700 transition"
                >
                    👨‍💼 Admin Login
                </Link>
                <Link
                    to="/candidate/login"
                    className="w-full sm:w-auto text-center bg-green-600 text-white px-6 py-3 rounded-full font-semibold shadow hover:bg-green-700 transition"
                >
                    🧑‍🎓 Candidate Login
                </Link>
            </div>
        </section>
    );
}

export default function ExamsPro() {
    const [openMenu, setOpenMenu] = useState<string | null>(null);
    const [mobileOpen, setMobileOpen] = useState(false);
    const { i18n } = useTranslation();
    useEffect(() => {
        const closeMenus = () => setOpenMenu(null);
        document.addEventListener("click", closeMenus);
        return () => document.removeEventListener("click", closeMenus);
    }, []);

    const toggleMenu = (menu: string) =>
        setOpenMenu(openMenu === menu ? null : menu);
    const toggleMobile = () => setMobileOpen(!mobileOpen);

    return (
        <main className="bg-white text-gray-900">
            {/* ---------------- Navbar ---------------- */}
           
            <header className="bg-white shadow-sm sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
                    {/* Logo */}
                    <div className="flex items-center space-x-3">
                        <img
                            src="/Images/ExamsPro.png"
                            alt="ExamsPro Logo"
                            className="w-[42px] h-auto rounded-lg shadow border border-gray-200"
                        />
                        <span className="text-xl font-bold text-gray-900">
                            SecureID <span className="text-indigo-600">ExamsPro</span>
                        </span>
                    </div>

                    {/* Desktop Nav */}
                    <nav
                        className="hidden md:flex items-center space-x-8 font-medium text-gray-700"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* UtilityBar */}
                        <div className="flex items-center gap-4 border-l pl-6 ml-4 border-gray-200">
                            <Link to="/login" className="hover:text-indigo-600">
                                Exams Admin Login
                            </Link>
                            <Link to="/candidate/login" className="hover:text-indigo-600">
                                Exam Taker Login
                            </Link>
                            <Link to="/solutions/examspro/support" className="hover:text-indigo-600">
                                Customer Support
                            </Link>
                        </div>


                        {/* Main sections */}
                        {["features", "services", "resources", "about"].map((menu) => (
                            <div key={menu} className="relative">
                                <button
                                    onClick={() => setOpenMenu(openMenu === menu ? null : menu)}
                                    className="flex items-center gap-1 hover:text-indigo-600 capitalize"
                                >
                                    {menu}
                                    <svg
                                        className="w-4 h-4 ml-1"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M19 9l-7 7-7-7"
                                        />
                                    </svg>
                                </button>

                                {openMenu === menu && (
                                    <div className="absolute left-0 mt-2 bg-white border border-gray-100 rounded-lg shadow-lg w-56 z-50">
                                        <ul className="py-2 text-sm text-gray-700">
                                            {menu === "features" && (
                                                <>
                                                    <li>
                                                        <Link
                                                            to="/solutions/examspro/build"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Build Exams
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/examspro/deliver"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Deliver Exams
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/examspro/biometric"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Biometric Validation
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/examspro/offline"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Offline Delivery
                                                        </Link>
                                                    </li>
                                                    <li>
                                                        <Link
                                                            to="/solutions/examspro/analytics"
                                                            className="block px-4 py-2 hover:bg-indigo-50 hover:text-indigo-700 transition"
                                                        >
                                                            Performance Analytics
                                                        </Link>
                                                    </li>
                                                </>
                                            )}
                                            {menu === "services" && (
                                                <>
                                                    <li><Link to="/services/exam-design" className="block px-4 py-2 hover:bg-gray-50">Exam Design & Development</Link></li>
                                                    <li><Link to="/services/security" className="block px-4 py-2 hover:bg-gray-50">Test Security Services</Link></li>
                                                </>
                                            )}
                                            {menu === "resources" && (
                                                <>
                                                    <li><Link to="/resources/help" className="block px-4 py-2 hover:bg-gray-50">Help Center</Link></li>
                                                    <li><Link to="/resources/library" className="block px-4 py-2 hover:bg-gray-50">Resource Library</Link></li>
                                                    <li><Link to="/resources/training" className="block px-4 py-2 hover:bg-gray-50">Training</Link></li>
                                                    <li><Link to="/resources/faq" className="block px-4 py-2 hover:bg-gray-50">FAQ</Link></li>
                                                </>
                                            )}
                                            {menu === "about" && (
                                                <>
                                                    <li><Link to="/about/why-secureid" className="block px-4 py-2 hover:bg-gray-50">Why SecureID</Link></li>
                                                    <li><Link to="/solutions/examspro/support" className="block px-4 py-2 hover:bg-gray-50">Contact Us</Link></li>
                                                </>
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        ))}


                    </nav>

                    {/* Mobile toggle */}
                    <button
                        onClick={toggleMobile}
                        className="md:hidden text-gray-700"
                    >
                        {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>

              
                {/* MOBILE MENU */}
                {mobileOpen && (
                    <div className="md:hidden bg-white border-t border-gray-200 shadow-inner max-h-[85vh] overflow-y-auto px-6 py-4 space-y-4">

                        {/* Main menu items */}
                        {MENU.map((item) => (
                            <div key={item.label}>
                                <div className="text-[15px] font-semibold text-gray-900 mb-2">
                                    {item.label}
                                </div>
                                <ul className="pl-3 mt-2 space-y-1 bg-indigo-50 rounded-md border border-indigo-100">
                                    {item.children.map((child) => (
                                        <li key={child.label}>
                                            <NavLink
                                                to={child.to}
                                                onClick={() => setMobileOpen(false)}
                                                className={({ isActive }) =>
                                                    [
                                                        "block w-full rounded-md px-3 py-2 text-[15px] transition font-medium !text-indigo-900",
                                                        isActive
                                                            ? "!bg-indigo-100 !text-indigo-800 font-semibold"
                                                            : "hover:!bg-indigo-100 hover:!text-indigo-700",
                                                    ].join(" ")
                                                }
                                            >
                                                {child.label}
                                            </NavLink>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        ))}

                        {/* UtilityBar Options */}
                        <div className="pt-4 border-t border-gray-200">
                            <p className="text-sm font-semibold text-gray-700 mb-2">Utility</p>
                            <div className="flex flex-col space-y-2 text-indigo-700 font-medium">
                                <Link to="/login" onClick={() => setMobileOpen(false)}>
                                    Exams Admin Login
                                </Link>
                                <Link to="/candidate/login" onClick={() => setMobileOpen(false)}>
                                    Exam Taker Login
                                </Link>
                                <Link to="/solutions/examspro/support" onClick={() => setMobileOpen(false)}>
                                    Customer Support
                                </Link>
                            </div>
                        </div>

                        {/* 🌎 Language Selector */}
                        <div className="pt-3 border-t mt-2 space-y-3 !text-indigo-900">
                            <div className="px-2">
                                <RegionLanguageMenu
                                    value={(i18n.language as LangCode) || "en"}
                                    onChange={(lng) => {
                                        i18n.changeLanguage(lng);
                                        setMobileOpen(false);
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

            </header>


            {/* ---------------- Hero ---------------- */}
            <section className="bg-gradient-to-br from-indigo-700 via-indigo-800 to-indigo-900 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
                    <div>
                        {/* NEW: AI badge */}
                        <div className="inline-flex items-center gap-2 bg-indigo-600/30 border border-indigo-400/40 text-indigo-100 px-3 py-1 rounded-full text-xs font-semibold mb-3">
                            <span className="inline-block w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" />
                            AI-Powered Testing
                        </div>

                        <h1 className="text-4xl md:text-5xl font-extrabold leading-tight">
                            The Premier <span className="text-indigo-300">Secure Exam Platform</span>
                        </h1>
                        <p className="mt-6 text-lg text-indigo-100">
                            Powerful tools to create & deliver secure exams—built for certification, licensing, education, and government.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            {/* Schedule a Demo */}
                            <Link
                                to="/contact"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                📅 Schedule a Demo
                            </Link>

                            {/* Get Started */}
                            <Link
                                to="/contact"
                                className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                Get Started
                            </Link>

                            {/* Download App */}
                            {/* Download App */}
                            <div className="flex flex-col sm:flex-row gap-3">
                                <a
                                    href="https://secureidtechnologies.com/downloads/ExamsPro.apk"
                                    download
                                    className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                                >
                                    Download Android App
                                </a>

                                <a
                                    href="https://secureidtechnologies.com/downloads/ExamsProInstaller.msix"
                                    download
                                    className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                                >
                                    Download Windows App
                                </a>
                            </div>


                            {/* Launch Live Demo */}
                            <a
                                href="https://appetize.io/app/b_6j6vlhwofemzpoaexa3nipubhy?device=pixel7&osVersion=13.0&toolbar=true"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 bg-indigo-700 hover:bg-indigo-600 text-white font-semibold px-6 py-3 rounded-full shadow-md transition"
                            >
                                🚀 Launch Live Demo
                            </a>
                        </div>
                    </div>

                    {/* RIGHT: Tablet mockup (replaces plain image) */}
                    <motion.div
                        initial={{ opacity: 0, x: 100 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="relative flex justify-center items-center"
                    >
                        <motion.div
                            animate={{ y: [0, -6, 0] }}
                            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                            className="relative"
                        >
                            {/* Gradient rim */}
                            <div className="relative bg-gradient-to-tr from-pink-500 via-red-500 to-purple-600 p-1 rounded-[2.5rem] shadow-2xl">
                                {/* Tablet body */}
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid md:grid-cols-2 gap-12 items-center text-center md:text-left">

                                    {/* Camera dot */}
                                    <div className="absolute top-2 left-1/2 -translate-x-1/2 w-3 h-3 bg-gray-700 rounded-full" />
                                    {/* Side buttons */}
                                    <div className="absolute -left-1 top-28 w-1 h-12 bg-gray-700 rounded-r" />
                                    <div className="absolute -left-1 top-44 w-1 h-20 bg-gray-700 rounded-r" />

                                    {/* Screen */}
                                    <div className="rounded-xl overflow-hidden shadow-xl bg-gray-100">
                                        <img
                                            src="/Images/examspro-tablet.jpg"
                                            alt="ExamsPro Interface"
                                            className="w-full h-auto object-cover"
                                        />
                                    </div>
                                </div>
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </section>

            {/* ---------------- ExamsPro Assist (AI) ---------------- */}
            <section className="bg-indigo-50/50 py-24">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <div className="order-2 md:order-1">
                        <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">
                            Introducing <span className="text-indigo-700">ExamsPro Assist</span>
                        </h2>
                        <p className="mt-4 text-gray-700 text-lg">
                            A guided assistant for blueprinting, item writing, review, and content organization—keeping your team in the driver’s seat.
                        </p>
                        <ul className="mt-6 space-y-2 text-gray-700">
                            <li>• Objective-aligned prompts and templates.</li>
                            <li>• Streamlined blueprinting and versioned reviews.</li>
                            <li>• Optional by design—use it when you want.</li>
                            <li>• Private by default—we don’t train on your data.</li>
                        </ul>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link
                                to="/resources/training"
                                className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700 shadow"
                            >
                                Learn about Assist
                            </Link>

                            <Link
                                to="/pricing"
                                className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold border border-indigo-200 hover:bg-indigo-200 shadow-sm"
                            >
                                View Pricing
                            </Link>

                            <Link
                                to="/solutions/examspro/support"
                                className="bg-white text-indigo-700 px-6 py-3 rounded-full font-semibold border border-indigo-200 hover:bg-indigo-50 shadow-sm"
                            >
                                Talk to an expert
                            </Link>
                        </div>
                    </div>
                    <div className="order-1 md:order-2">
                        <div className="rounded-2xl overflow-hidden shadow-2xl bg-white border border-indigo-100">
                            <img src="/Images/exam-bg.jpg" alt="ExamsPro Assist" className="w-full h-auto" />
                        </div>
                    </div>
                </div>
            </section>

            {/* ---------------- Build / Deliver / Manage Cards ---------------- */}
            <section className="bg-gray-50 py-20 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 text-center">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-14">
                        ExamsPro Gives You Everything You Need To…
                    </h2>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">
                        {[
                            {
                                title: "Build Exams",
                                desc: "Author items, manage banks, and publish structured forms with reviews.",
                                path: "/solutions/examspro/build",
                            },
                            {
                                title: "Deliver Exams",
                                desc: "Deliver online/offline with device controls, monitoring, and biometrics.",
                                path: "/solutions/examspro/deliver",
                            },
                            {
                                title: "Manage Exams",
                                desc: "Track results, analyze item performance, and export audit-ready reports.",
                                path: "/solutions/examspro/analytics",
                            },
                        ].map(({ title, desc, path }) => (
                            <div
                                key={title}
                                className="bg-white rounded-2xl shadow-sm hover:shadow-md p-8 border border-gray-100 transition"
                            >
                                <div className="flex justify-center mb-4">
                                    <div className="p-3 rounded-full bg-indigo-50">
                                        <ShieldCheck className="w-10 h-10 text-indigo-600" />
                                    </div>
                                </div>
                                <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
                                <p className="text-gray-600 mb-6">{desc}</p>
                                <Link
                                    to={path}
                                    className="inline-block bg-indigo-600 text-white px-6 py-2 rounded-full hover:bg-indigo-700 transition"
                                >
                                    Learn More
                                </Link>
                            </div>
                        ))}
                    </div>
                </div>
            </section>


            {/* ---------------- Security ---------------- */}
            <section className="bg-gray-50 py-20 border-t border-gray-100">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ x: -100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
                            Built With a Suite of Powerful Security Features
                        </h2>
                        <ul className="space-y-3 text-gray-700">
                            <li>• Manage exam access with roles and 2FA.</li>
                            <li>• Real-time encryption for items and results.</li>
                            <li>• Proctor event logs & biometric validation.</li>
                            <li>• SOC 2 Type II and ISO 27001 aligned.</li>
                        </ul>
                        <div className="mt-6 flex gap-4">
                            <Link to="/security" className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700">Learn More</Link>
                            <Link to="/compliance" className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-indigo-200">View Compliance</Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ x: 100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                        className="flex justify-center"
                    >
                        <img
                            src="/Images/fingerprint-scanning.jpg"
                            alt="ExamsPro Security"
                            className="rounded-2xl shadow-xl border border-gray-200"
                        />
                    </motion.div>
                </div>
            </section>

            {/* ---------------- Integrations ---------------- */}
            <section className="bg-white py-24">
                <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ x: -100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <img
                            src="/Images/contract-bg.jpg"
                            alt="Integrations"
                            className="rounded-2xl shadow-2xl border border-gray-200"
                        />
                    </motion.div>

                    <motion.div
                        initial={{ x: 100, opacity: 0 }}
                        whileInView={{ x: 0, opacity: 1 }}
                        transition={{ duration: 0.8 }}
                        viewport={{ once: true }}
                    >
                        <h2 className="text-3xl md:text-4xl font-extrabold mb-4">
                            Configure and Integrate With Flexibility
                        </h2>
                        <ul className="space-y-3 text-gray-700">
                            <li>• Integrate with Canvas, Blackboard, or Skilljar.</li>
                            <li>• Connect with Slack, SendGrid, Examity, or ProctorU.</li>
                            <li>• Create custom automation with simple webhooks.</li>
                            <li>• Work with our hands-on integration support team.</li>
                        </ul>
                        <div className="mt-6 flex gap-4">
                            <Link to="/integrations" className="bg-indigo-600 text-white px-6 py-3 rounded-full font-semibold hover:bg-indigo-700">Explore Integrations</Link>
                            <Link to="/contact" className="bg-indigo-100 text-indigo-700 px-6 py-3 rounded-full font-semibold hover:bg-indigo-200">Talk to Support</Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ---------------- Ideal For ---------------- */}
            <section className="bg-white py-24">
                <div className="max-w-7xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">
                        ExamsPro is Ideal For
                    </h2>
                    <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {[
                            ["IT Certifications", "icons/cert-badge.svg"],
                            ["Corporate Training & Compliance", "icons/compliance.svg"],
                            ["Professional Certifications", "icons/pro-cert.svg"],
                            ["Academic Admissions", "icons/admissions.svg"],
                            ["Higher Education", "icons/higher-ed.svg"],
                            ["Government Programs", "icons/government.svg"],
                            ["Health Care", "icons/health.svg"],
                            ["Pre-employment", "icons/preemployment.svg"],
                        ].map(([label, icon]) => (
                            <div key={label as string} className="p-6 bg-gray-50 rounded-xl border hover:shadow transition flex items-center gap-4">
                                <img src={`/${icon}`} alt="" className="h-8 w-8" />
                                <span className="font-semibold text-gray-900">{label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- Testimonials ---------------- */}
            <section className="bg-gray-50 py-24">
                <div className="max-w-6xl mx-auto px-6">
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 text-center">
                        Why Our Clients Love ExamsPro
                    </h2>
                    <div className="mt-12 grid md:grid-cols-3 gap-6">
                        {[
                            {
                                quote:
                                    "We transitioned our tests quickly with strong guidance and support. The team was hands-on and responsive throughout.",
                                name: "Program Director",
                                org: "National Training Council",
                            },
                            {
                                quote:
                                    "Item banking and review are intuitive—even for new contributors—which sped up our development cycle.",
                                name: "Exam Consultant",
                                org: "Global Software Co.",
                            },
                            {
                                quote:
                                    "Security and analytics are first-class. We can defend items, monitor exams, and act on trends confidently.",
                                name: "Assessment Lead",
                                org: "Healthcare Certification Board",
                            },
                        ].map((t) => (
                            <figure key={t.quote} className="p-6 bg-white rounded-2xl shadow-sm border">
                                <blockquote className="text-gray-700">“{t.quote}”</blockquote>
                                <figcaption className="mt-4 text-sm text-gray-500">
                                    <span className="font-semibold text-gray-900">{t.name}</span>, {t.org}
                                </figcaption>
                            </figure>
                        ))}
                    </div>
                </div>
            </section>

            {/* ---------------- Two-Path CTA ---------------- */}
            <section className="bg-indigo-700 text-white py-20">
                <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-6">
                    <div className="bg-indigo-600/40 rounded-2xl p-8 border border-indigo-500">
                        <h3 className="text-2xl font-bold">I want to build & deliver my own secure exams</h3>
                        <p className="mt-3 text-indigo-100">
                            Use ExamsPro to author items, publish forms, deliver online/offline, and analyze results.
                        </p>
                        <Link
                            to="/features/build"
                            className="inline-block mt-6 border border-white text-white font-semibold px-5 py-2 rounded-full hover:bg-white hover:text-indigo-700 transition"
                        >
                            Learn More
                        </Link>
                    </div>
                    <div className="bg-indigo-600/40 rounded-2xl p-8 border border-indigo-500">
                        <h3 className="text-2xl font-bold">I want a psychometrics team to build it for us</h3>
                        <p className="mt-3 text-indigo-100">
                            Partner with our testing & security experts for full-service design, development, and delivery.
                        </p>
                        <Link
                            to="/services/exam-design"
                            className="inline-block mt-6 border border-white text-white font-semibold px-5 py-2 rounded-full hover:bg-white hover:text-indigo-700 transition"
                        >
                            Learn More
                        </Link>
                    </div>
                </div>
            </section>

            {/* ---------------- Footer ---------------- */}
            <footer className="bg-gray-900 text-gray-300 py-16">
                <div className="max-w-7xl mx-auto px-6 grid sm:grid-cols-2 md:grid-cols-4 gap-10">
                    <div>
                        <h3 className="text-xl font-bold text-white mb-3">SecureID Technologies</h3>
                        <p className="text-sm mb-4">
                            ExamsPro — The Secure Testing Platform for Agencies & Enterprises.
                        </p>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-3">Company</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/about">About SecureID</Link></li>
                            <li><Link to="/careers">Careers</Link></li>
                            <li><Link to="/contact">Contact Us</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-3">Resources</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/help">Help Center</Link></li>
                            <li><Link to="/docs">Documentation</Link></li>
                            <li><Link to="/privacy">Privacy & Security</Link></li>
                        </ul>
                    </div>
                    <div>
                        <h4 className="font-semibold text-white mb-3">Products</h4>
                        <ul className="space-y-1 text-sm">
                            <li><Link to="/solutions/examspro">ExamsPro</Link></li>
                            <li><Link to="/solutions/dactylscan">DactylScan</Link></li>
                            <li><Link to="/solutions/background">Background Checks</Link></li>
                        </ul>
                    </div>
                </div>
                <div className="border-t border-gray-700 mt-10 pt-6 text-center text-sm">
                    <p>
                        © 2025 SecureID Technologies LLC — All Rights Reserved.{" "}
                        <Link to="/legal" className="text-indigo-400 hover:underline mx-1">Legal Stuff</Link> |{" "}
                        <Link to="/privacy" className="text-indigo-400 hover:underline mx-1">Privacy Policy</Link>
                    </p>
                </div>
            </footer>
        </main>
    );
}